import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FieldInput } from '../../lib/api/types/esign.interfaces'
import type { EnvelopeField } from '../../lib/api/types/esign.interfaces'
import {
	type Box,
	type Preset,
	type Tool,
	DEFAULT_FIELD,
	dedupeEnvelopeFields,
	envelopeFieldsToBoxes,
	genId,
	isTextTool,
	metaForPreset,
} from '../../lib/utils/esign'

type FieldLimitKey =
	| 'TEXT'
	| 'SIGNATURE'
	| 'INITIALS'
	| 'NAME'
	| 'EMAIL'
	| 'COMPANY'
	| 'TITLE'
	| 'PHONE'
	| 'DATE'
	| 'ADDRESS'
	| 'CUSTOM'
type RectFieldLimitsPercent = {
	shape: 'rectPercent'
	minWidth: number
	minHeight: number
	maxWidth: number
	maxHeight: number
}
type RectFieldLimitsPx = {
	shape: 'rectPx'
	minWidth: number
	minHeight: number
	maxWidth: number
	maxHeight: number
}

type FieldDefaultSizePx = {
	width: number
	height: number
}

const RECT_TEXT_SINGLE_LINE_LIMITS: RectFieldLimitsPx = {
	shape: 'rectPx',
	minWidth: 80,
	minHeight: 34,
	maxWidth: 10000,
	maxHeight: 34,
}

const LINE_HEIGHT_PX = 34
const ADDRESS_LINES = 3

const FIELD_SIZE_LIMITS: Record<
	FieldLimitKey,
	RectFieldLimitsPercent | RectFieldLimitsPx
> = {
	TEXT: {
		shape: 'rectPx',
		minWidth: 80,
		minHeight: 34,
		maxWidth: 10000,
		maxHeight: 10000,
	},
	EMAIL: {
		...RECT_TEXT_SINGLE_LINE_LIMITS,
	},
	SIGNATURE: {
		shape: 'rectPx',
		minWidth: 80,
		minHeight: LINE_HEIGHT_PX * 1,
		maxWidth: 10000,
		maxHeight: LINE_HEIGHT_PX * 3,
	},
	INITIALS: {
		shape: 'rectPx',
		minWidth: 34,
		minHeight: 34,
		maxWidth: 56,
		maxHeight: 34,
	},
	NAME: { ...RECT_TEXT_SINGLE_LINE_LIMITS },
	COMPANY: { ...RECT_TEXT_SINGLE_LINE_LIMITS },
	TITLE: { ...RECT_TEXT_SINGLE_LINE_LIMITS },
	PHONE: {
		shape: 'rectPx',
		minWidth: 120,
		minHeight: 34,
		maxWidth: 220,
		maxHeight: 34,
	},
	DATE: {
		shape: 'rectPx',
		minWidth: 120,
		minHeight: 34,
		maxWidth: 100,
		maxHeight: 34,
	},
	ADDRESS: {
		shape: 'rectPx',
		minWidth: 120,
		minHeight: 34,
		maxWidth: 10000,
		maxHeight: LINE_HEIGHT_PX * ADDRESS_LINES,
	},
	CUSTOM: {
		shape: 'rectPx',
		minWidth: 80,
		minHeight: 34,
		maxWidth: 10000,
		maxHeight: 10000,
	},
}

const SINGLE_LINE_DEFAULT_SIZE: FieldDefaultSizePx = {
	width: 240,
	height: LINE_HEIGHT_PX,
}

const FIELD_DEFAULT_SIZE_PX: Record<FieldLimitKey, FieldDefaultSizePx> = {
	TEXT: { width: 220, height: LINE_HEIGHT_PX },
	EMAIL: { ...SINGLE_LINE_DEFAULT_SIZE },
	SIGNATURE: {
		width: 240,
		height: LINE_HEIGHT_PX * 2,
	},
	INITIALS: { width: 52, height: 44 },
	NAME: { ...SINGLE_LINE_DEFAULT_SIZE },
	COMPANY: { ...SINGLE_LINE_DEFAULT_SIZE },
	TITLE: { ...SINGLE_LINE_DEFAULT_SIZE },
	PHONE: { width: 180, height: LINE_HEIGHT_PX },
	DATE: { width: 90, height: LINE_HEIGHT_PX },
	ADDRESS: {
		width: 260,
		height: LINE_HEIGHT_PX * ADDRESS_LINES,
	},
	CUSTOM: { width: 350, height: 250 },
}

const FIELD_LIMIT_KEY_BY_TYPE_OR_PRESET: Partial<
	Record<Tool | Box['type'] | Preset, FieldLimitKey>
> = {
	SIGNATURE: 'SIGNATURE',
	INITIALS: 'INITIALS',
	NAME: 'NAME',
	EMAIL: 'EMAIL',
	COMPANY: 'COMPANY',
	TITLE: 'TITLE',
	PHONE: 'PHONE',
	DATE: 'DATE',
	ADDRESS: 'ADDRESS',
	CUSTOM: 'CUSTOM',
}

function resolveFieldLimitKey(
	fieldType?: Tool | Box['type'] | null,
	preset?: Preset,
): FieldLimitKey {
	if (fieldType && FIELD_LIMIT_KEY_BY_TYPE_OR_PRESET[fieldType]) {
		return FIELD_LIMIT_KEY_BY_TYPE_OR_PRESET[fieldType]
	}
	if (preset && FIELD_LIMIT_KEY_BY_TYPE_OR_PRESET[preset]) {
		return FIELD_LIMIT_KEY_BY_TYPE_OR_PRESET[preset]
	}
	return 'TEXT'
}

export interface UseFieldPlacerStateProps {
	initialFields?: EnvelopeField[] | null
	onSave?: (
		nextFields: FieldInput[],
	) => boolean | void | Promise<boolean | void>
	returnAllFields?: boolean
	requiredSignatureRecipientIds?: string[]
}

export function useFieldPlacerState({
	initialFields,
	onSave,
	returnAllFields = false,
	requiredSignatureRecipientIds = [],
}: UseFieldPlacerStateProps) {
	const [boxes, setBoxes] = useState<Box[]>([])
	const [submitError, setSubmitError] = useState<string | null>(null)
	const hasAppliedInitialRef = useRef(false)
	const initialBoxIdsRef = useRef<Set<string>>(new Set())
	const lastInitialKeyRef = useRef<string>('')
	const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map())
	const skipNextPageClickRef = useRef(false)

	useEffect(() => {
		if (!initialFields?.length) {
			lastInitialKeyRef.current = ''
			return
		}
		const deduped = dedupeEnvelopeFields(initialFields)
		const key = deduped
			.map((f) => f.id ?? `${f.page}-${f.positionX}-${f.positionY}`)
			.sort()
			.join(',')
		if (hasAppliedInitialRef.current && lastInitialKeyRef.current === key)
			return
		lastInitialKeyRef.current = key
		hasAppliedInitialRef.current = true
		const boxesFromApi = envelopeFieldsToBoxes(deduped)
		initialBoxIdsRef.current = new Set(boxesFromApi.map((b) => b.id))
		setBoxes(boxesFromApi)
	}, [initialFields])

	const setPageRef = useCallback(
		(pageNumber: number, el: HTMLDivElement | null) => {
			if (!el) {
				pageRefs.current.delete(pageNumber)
			} else {
				pageRefs.current.set(pageNumber, el)
			}
		},
		[],
	)

	const clamp = useCallback((value: number, min: number, max: number) => {
		if (max < min) return min
		return Math.min(Math.max(value, min), max)
	}, [])

	const getResizeLimits = useCallback(
		(
			pageNumber: number,
			fieldType?: Tool | Box['type'] | null,
			preset?: Preset,
		) => {
			const limitKey = resolveFieldLimitKey(fieldType, preset)
			const limitsConfig = FIELD_SIZE_LIMITS[limitKey]
			const el = pageRefs.current.get(pageNumber)
			if (!el) {
				return {
					minWidth: 0,
					minHeight: 0,
					maxWidth: Infinity,
					maxHeight: Infinity,
				}
			}

			const rect = el.getBoundingClientRect()
			// if (limitsConfig.shape === 'square') {
			// 	const baseSide = Math.min(rect.width, rect.height)
			// 	const minSide = (limitsConfig.minSide / 100) * baseSide
			// 	const maxSide = (limitsConfig.maxSide / 100) * baseSide
			// 	return {
			// 		minWidth: minSide,
			// 		minHeight: minSide,
			// 		maxWidth: maxSide,
			// 		maxHeight: maxSide,
			// 	}
			// }

			if (limitsConfig.shape === 'rectPx') {
				const rectLimits = {
					minWidth: limitsConfig.minWidth,
					minHeight: limitsConfig.minHeight,
					maxWidth: limitsConfig.maxWidth,
					maxHeight: limitsConfig.maxHeight,
				}
				if (limitKey === 'CUSTOM') {
					rectLimits.maxHeight = Math.min(
						rectLimits.maxHeight,
						rect.height * 0.5,
					)
				}
				return rectLimits
			}

			return {
				minWidth: (limitsConfig.minWidth / 100) * rect.width,
				minHeight: (limitsConfig.minHeight / 100) * rect.height,
				maxWidth: (limitsConfig.maxWidth / 100) * rect.width,
				maxHeight: (limitsConfig.maxHeight / 100) * rect.height,
			}
		},
		[],
	)

	const pxToPercent = useCallback(
		(pageNumber: number, x: number, y: number, w: number, h: number) => {
			const el = pageRefs.current.get(pageNumber)
			if (!el) return DEFAULT_FIELD
			const rect = el.getBoundingClientRect()
			const round = (n: number) => Math.round(n * 1000) / 1000
			return {
				pageX: round((x / rect.width) * 100),
				pageY: round((y / rect.height) * 100),
				pageWidth: round((w / rect.width) * 100),
				pageHeight: round((h / rect.height) * 100),
			}
		},
		[],
	)

	const percentToPx = useCallback((pageNumber: number, b: Box) => {
		const el = pageRefs.current.get(pageNumber)
		if (!el) return { x: 0, y: 0, w: 0, h: 0 }
		const rect = el.getBoundingClientRect()
		return {
			x: (b.pageX / 100) * rect.width,
			y: (b.pageY / 100) * rect.height,
			w: (b.pageWidth / 100) * rect.width,
			h: (b.pageHeight / 100) * rect.height,
		}
	}, [])

	const boxesToFieldInput = useCallback(
		(boxesToConvert: Box[]): FieldInput[] => {
			const buildFieldMeta = (box: Box) => {
				const preservedFieldMeta = { __fieldId: box.id } as Record<
					string,
					unknown
				>

				if (box.type === 'SIGNATURE') {
					return preservedFieldMeta
				}

				const preset = box.type === 'INITIALS' ? 'INITIALS' : box.preset
				const meta = metaForPreset(preset)

				if (box.type === 'TEXT' && box.preset) {
					return { ...preservedFieldMeta, ...meta, preset: box.preset }
				}

				return { ...preservedFieldMeta, ...meta }
			}

			return boxesToConvert.map((b) => {
				const base = {
					recipientId: b.recipientId,
					pageNumber: b.pageNumber,
					pageX: b.pageX,
					pageY: b.pageY,
					pageWidth: b.pageWidth,
					pageHeight: b.pageHeight,
				} as const
				if (b.type === 'SIGNATURE') {
					return {
						...base,
						type: 'SIGNATURE',
						fieldMeta: buildFieldMeta(b),
					} as FieldInput
				}
				if (b.type === 'INITIALS') {
					return {
						...base,
						type: 'INITIALS',
						fieldMeta: buildFieldMeta(b),
					} as FieldInput
				}
				return {
					...base,
					type: 'TEXT',
					fieldMeta: buildFieldMeta(b),
				} as FieldInput
			})
		},
		[],
	)

	const addBoxAt = useCallback(
		(
			pageNumber: number,
			clientX: number,
			clientY: number,
			selectedTool: Tool | null,
			selectedRecipientId: string | null,
		) => {
			if (!selectedTool || !selectedRecipientId) return
			const el = pageRefs.current.get(pageNumber)
			if (!el) return

			const rect = el.getBoundingClientRect()
			const isText = isTextTool(selectedTool)
			const limitKey = resolveFieldLimitKey(selectedTool)
			const defaultSize = FIELD_DEFAULT_SIZE_PX[limitKey]
			const limits = getResizeLimits(pageNumber, selectedTool)
			const defaultW = clamp(
				defaultSize.width,
				limits.minWidth,
				limits.maxWidth,
			)
			const defaultH = clamp(
				defaultSize.height,
				limits.minHeight,
				limits.maxHeight,
			)

			const xPx = Math.max(
				0,
				Math.min(clientX - rect.left - defaultW / 2, rect.width - defaultW),
			)
			const yPx = Math.max(
				0,
				Math.min(clientY - rect.top - defaultH / 2, rect.height - defaultH),
			)

			const pct = pxToPercent(pageNumber, xPx, yPx, defaultW, defaultH)

			const asBox: Box = {
				id: genId(),
				recipientId: selectedRecipientId,
				type: isText ? 'TEXT' : (selectedTool as 'SIGNATURE' | 'INITIALS'),
				preset: isText ? (selectedTool as Preset) : undefined,
				pageNumber,
				...pct,
			}

			setBoxes((bs) => [...bs, asBox])
		},
		[clamp, getResizeLimits, pxToPercent],
	)

	const removeBox = useCallback((id: string) => {
		skipNextPageClickRef.current = true
		setBoxes((bs) => bs.filter((b) => b.id !== id))
	}, [])

	const onDragResizeStop = useCallback(
		(
			b: Box,
			pageNumber: number,
			x: number,
			y: number,
			w: number,
			h: number,
		) => {
			const el = pageRefs.current.get(pageNumber)
			if (!el) return

			const rect = el.getBoundingClientRect()
			const limits = getResizeLimits(pageNumber, b.type, b.preset)
			const boundedW = clamp(w, limits.minWidth, limits.maxWidth)
			const boundedH = clamp(h, limits.minHeight, limits.maxHeight)
			const boundedX = clamp(x, 0, rect.width - boundedW)
			const boundedY = clamp(y, 0, rect.height - boundedH)
			const pct = pxToPercent(
				pageNumber,
				boundedX,
				boundedY,
				boundedW,
				boundedH,
			)
			setBoxes((bs) => bs.map((x) => (x.id === b.id ? { ...x, ...pct } : x)))
		},
		[clamp, getResizeLimits, pxToPercent],
	)

	const handleSave = useCallback(async (): Promise<boolean> => {
		const targetBoxes = returnAllFields
			? boxes
			: boxes.filter((b) => !initialBoxIdsRef.current.has(b.id))
		const nextFields = boxesToFieldInput(targetBoxes)
		const saved = await onSave?.(nextFields)
		return saved !== false
	}, [boxes, boxesToFieldInput, onSave, returnAllFields])

	const getFieldsForSubmit = useCallback(() => {
		const targetBoxes = returnAllFields
			? boxes
			: boxes.filter((b) => !initialBoxIdsRef.current.has(b.id))

		return boxesToFieldInput(targetBoxes)
	}, [boxes, boxesToFieldInput, returnAllFields])

	const hasSignatureField = useMemo(
		() => boxes.some((b) => b.type === 'SIGNATURE'),
		[boxes],
	)

	const hasRequiredSignatureFields = useMemo(() => {
		if (!requiredSignatureRecipientIds.length) {
			return hasSignatureField
		}

		const recipientsWithSignature = new Set(
			boxes
				.filter((box) => box.type === 'SIGNATURE')
				.map((box) => String(box.recipientId)),
		)

		return requiredSignatureRecipientIds.every((recipientId) =>
			recipientsWithSignature.has(String(recipientId)),
		)
	}, [boxes, hasSignatureField, requiredSignatureRecipientIds])

	const canSubmit = useMemo(
		() => boxes.length > 0 && hasRequiredSignatureFields,
		[boxes.length, hasRequiredSignatureFields],
	)

	const validateFields = useCallback(() => {
		if (!hasRequiredSignatureFields) {
			setSubmitError(
				requiredSignatureRecipientIds.length
					? 'Each signer must have at least one Signature field before sending.'
					: 'At least one Signature field is required before sending.',
			)
			return false
		}

		setSubmitError(null)
		return true
	}, [hasRequiredSignatureFields, requiredSignatureRecipientIds.length])

	useEffect(() => {
		if (!boxes.length) {
			setSubmitError(null)
			return
		}
		if (hasRequiredSignatureFields) {
			setSubmitError(null)
		}
	}, [boxes, hasRequiredSignatureFields])

	const clearBoxes = useCallback(() => {
		setBoxes([])
		setSubmitError(null)
	}, [])

	return {
		boxes,
		clearBoxes,
		setPageRef,
		pageRefs,
		skipNextPageClickRef,
		percentToPx,
		getResizeLimits,
		addBoxAt,
		removeBox,
		onDragResizeStop,
		handleSave,
		getFieldsForSubmit,
		validateFields,
		canSubmit,
		hasSignatureField,
		hasRequiredSignatureFields,
		submitError,
		setSubmitError,
	}
}
